// client/src/components/common/ProtectedRoute.js
import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import LoadingSpinner from './LoadingSpinner';

/**
 * ProtectedRoute ensures:
 * 1. Auth state is fully loaded before rendering or redirecting.
 * 2. Redirects to the correct login page if not authenticated.
 * 3. Role-based access control is enforced.
 *
 * @param {React.ReactNode} children - Component(s) to render if access is allowed.
 * @param {string} requiredRole - Optional. Required user role ('business' or 'wholesaler').
 */
const ProtectedRoute = ({ children, requiredRole }) => {
  const { isAuthenticated, loading: authLoading, user } = useAuth();
  const location = useLocation();

  // 1️⃣ Show loading spinner while auth state is initializing
  if (authLoading) {
    return (
      <div className="flex justify-center items-center min-h-screen">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  // 2️⃣ Redirect to login if not authenticated
  if (!isAuthenticated) {
    if (requiredRole === 'wholesaler') {
      return <Navigate to="/wholesaler/login" replace />;
    }
    return <Navigate to="/login" replace />;
  }

  // 2.5️⃣ First-login gate: force password reset + T&C acceptance
  const mustCompleteOnboarding = Boolean(user?.mustResetPassword || user?.mustAcceptTerms);
  if (mustCompleteOnboarding && location.pathname !== '/first-login') {
    return <Navigate to="/first-login" replace />;
  }

  // 3️⃣ If a requiredRole is specified, ensure the user's role matches
  if (requiredRole && user?.role !== requiredRole) {
    // Instead of sending immediately to /unauthorized, redirect to their dashboard
    if (user?.role === 'business') {
      return <Navigate to="/dashboard" replace />;
    } else if (user?.role === 'wholesaler') {
      return <Navigate to="/wholesaler/dashboard" replace />;
    }
    // fallback
    return <Navigate to="/" replace />;
  }

  // 4️⃣ All checks passed — render the protected content
  return children;
};

export default ProtectedRoute;
